import axios from 'axios';
import { Lang } from '../../../../../../../../../shared/type';

/**
 * 각 SNS 플랫폼의 embed 처리를 담당하는 유틸리티 클래스
 * 레거시 코드의 embed 처리 로직을 이식한 버전
 */
export class EmbedProcessors {
  /**
   * YouTube URL을 embed 형식으로 변환합니다.
   */
  static makeYoutubeEmbedUrl(url: string): string {
    if (!url) return '';

    // URL 이스케이프 처리 (기존 UtilS.escape 대체)
    url = url.trim();

    const embedUrl = 'https://www.youtube.com/embed/';
    const embedRegexp = /embed\/+/;
    const matchRegexp = /watch\?v=([^&]+)/;
    const shortRegexp = /shorts\/([^&|?]+)/;

    // 이미 embed URL인 경우
    if (embedRegexp.test(url)) {
      return url;
    }

    // 일반 youtube.com/watch?v= 형식
    if (matchRegexp.test(url)) {
      const match = url.match(matchRegexp);
      if (match && match[1]) {
        return embedUrl + match[1];
      }
    }

    // YouTube Shorts 형식
    if (shortRegexp.test(url)) {
      const match = url.match(shortRegexp);
      if (match && match[1]) {
        return embedUrl + match[1];
      }
    }

    // youtu.be 단축 URL 처리
    const youtubeId = url.replace(
      /(https?:\/\/(www\.)?)?youtube\.com\/watch\?v=|(https?:\/\/(www\.)?)?youtu\.be\//,
      '',
    );

    return embedUrl + youtubeId;
  }

  /**
   * YouTube embed HTML을 생성합니다.
   */
  static makeYoutubeEmbedHtml(youtubeEmbed: string): string {
    if (!youtubeEmbed) return '';

    const embedUrl = this.makeYoutubeEmbedUrl(youtubeEmbed);

    return `
      <iframe
        src="${embedUrl}"
        style="border: none; width: 100%; height: 100%;"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
        loading="lazy"
      ></iframe>
    `;
  }

  /**
   * Instagram embed를 처리합니다.
   */
  static async processInstagramEmbed(
    url: string,
    lang = Lang.kr,
  ): Promise<{
    success: boolean;
    html?: string;
    error?: string;
  }> {
    if (!url || !url.includes('instagram.com')) {
      return { success: false, error: '올바른 Instagram URL을 입력해주세요.' };
    }

    try {
      const response = await axios.get(`/${lang}/@api/insta/embed`, {
        params: { url },
      });

      return {
        success: true,
        html: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: 'Instagram 게시글을 불러올 수 없습니다. URL을 확인해주세요.',
      };
    }
  }

  /**
   * Twitter/X embed를 처리합니다.
   * 레거시 코드의 Twitter 처리 로직을 이식
   */
  static async processTwitterEmbed(url: string): Promise<{
    success: boolean;
    tweetId?: string;
    error?: string;
  }> {
    if (!url || !/(x\.com|twitter\.com)/.test(url)) {
      return { success: false, error: '올바른 X(Twitter) URL을 입력해주세요.' };
    }

    // URL에서 트윗 ID 추출
    const tweetId = url
      .split('/')
      .pop()
      ?.replace(/\?.+|#.+/, '');

    if (!tweetId || !/^\d+$/.test(tweetId)) {
      return { success: false, error: '트윗 ID를 찾을 수 없습니다.' };
    }

    return {
      success: true,
      tweetId,
    };
  }

  /**
   * Facebook embed를 처리합니다.
   */
  static async processFacebookEmbed(url: string): Promise<{
    success: boolean;
    html?: string;
    error?: string;
  }> {
    if (!url || !url.includes('facebook.com')) {
      return { success: false, error: '올바른 Facebook URL을 입력해주세요.' };
    }

    // Facebook embed는 클라이언트에서 XFBML.parse() 호출 후 처리
    return {
      success: true,
      html: this.makeFacebookEmbedHtml(url),
    };
  }

  /**
   * Facebook embed HTML을 생성합니다.
   */
  static makeFacebookEmbedHtml(fbEmbed: string): string {
    if (!fbEmbed) return '';

    // URL 이스케이프 처리
    const escapedUrl = fbEmbed.replace(/[&<>"']/g, (match) => {
      const escapeMap: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
      };
      return escapeMap[match] || match;
    });

    return `<div class="fb-post" data-href="${escapedUrl}" data-width="500"></div>`;
  }

  /**
   * TikTok embed를 처리합니다.
   */
  static async processTikTokEmbed(url: string): Promise<{
    success: boolean;
    html?: string;
    error?: string;
  }> {
    // requiresClientRender 속성 제거
    const tiktokPattern = /tiktok\.com\/@[\w.-]+\/video\/\d+|vm\.tiktok\.com\/[\w]+/;

    if (!url || !tiktokPattern.test(url)) {
      return { success: false, error: '올바른 TikTok URL을 입력해주세요.' };
    }

    const normalizedUrl = url.split('?')[0]?.split('#')[0] ?? url;

    return {
      success: true,
      html: this.makeTikTokEmbedHtml(normalizedUrl),
    };
  }

  /**
   * TikTok embed HTML을 iframe으로 생성합니다.
   */
  static makeTikTokEmbedHtml(tiktokEmbed: string): string {
    if (!tiktokEmbed) return '';

    const videoId = this.extractTikTokVideoId(tiktokEmbed);
    const username = this.extractTikTokUsername(tiktokEmbed);

    if (!videoId) {
      return `<p>올바른 TikTok URL이 아닙니다.</p>`;
    }

    return `
    <iframe
      src="https://www.tiktok.com/embed/v2/${videoId}?referrer=${encodeURIComponent(window.location.origin)}"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; microphone; camera"
      allowfullscreen
      style="border: none;"
      loading="lazy"
      title="TikTok video by @${username}"
      referrerpolicy="strict-origin-when-cross-origin"
    ></iframe>
  `;
  }

  static extractTikTokVideoId(url: string): string {
    const match = url.match(/\/video\/(\d+)/);
    return match?.[1] ?? '';
  }

  static extractTikTokUsername(url: string): string {
    if (!url) return 'tiktok';

    const match = url.match(/@([\w.-]+)\/video/);
    return match?.[1] ?? 'tiktok';
  }
}
